import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { useInfiniteQuery, useQuery, useMutation } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { VoiceToTextTextarea } from "@/components/voice-field";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Handshake, Plus, Search, Trash2, ArrowRightCircle, ChevronDown,
  ArrowUpDown, ArrowUp, ArrowDown, Loader2, Mail, Phone, X, MoreHorizontal,
  FileText, CheckCircle2, Settings,
} from "lucide-react";
import { ManageSponsorTypesDialog } from "@/components/manage-sponsor-types-dialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useSponsorHubAccess } from "@/hooks/use-sponsor-hub-access";
import { cn } from "@/lib/utils";
import type { SponsorType, SponsorLead, SponsorClient, NlFilterSpec, Region } from "@shared/schema";
import { SmartSearchBar } from "@/components/smart-search-bar";

// ── Column widths ─────────────────────────────────────────────────────────────
const LEAD_COL_WIDTHS: Record<string, number> = {
  select: 40, leadName: 180, contactName: 140, phone: 130, email: 180,
  sponsorType: 130, rating: 70, rep: 100, city: 100, state: 60, zip: 70,
  optOut: 70, status: 100, lastContacted: 110, address: 180, region: 120, talent: 120, notes: 200,
};
const CLIENT_COL_WIDTHS: Record<string, number> = {
  select: 40, accountName: 200, contactName: 140, phone: 130, email: 180,
  sponsorType: 130, rep: 100, city: 100, state: 60, zip: 70, optOut: 70,
  notes: 180, address: 180, region: 120, talent: 120, associatedEvent: 200,
};

// ── Small helpers ─────────────────────────────────────────────────────────────
function ColHeader({
  label, sortKey, sort, dir, onSort,
}: { label: string; sortKey: string; sort: string; dir: string; onSort: (k: string) => void }) {
  const active = sort === sortKey;
  return (
    <button
      className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground truncate"
      onClick={() => onSort(sortKey)}
      data-testid={`col-header-${sortKey}`}
    >
      {label}
      {active ? (dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
    </button>
  );
}

function CellInput({
  value, onBlur, placeholder, className,
}: { value: string; onBlur: (v: string) => void; placeholder?: string; className?: string }) {
  const [val, setVal] = useState(value);
  useEffect(() => setVal(value), [value]);
  return (
    <input
      className={cn(
        "w-full h-full px-2 text-sm bg-transparent border-0 outline-none focus:ring-1 focus:ring-primary/50 rounded-none truncate",
        className
      )}
      value={val ?? ""}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if (val !== value) onBlur(val); }}
      placeholder={placeholder}
    />
  );
}

// ── Add/Edit Lead Dialog ──────────────────────────────────────────────────────
interface LeadDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing?: SponsorLead | null;
  types: SponsorType[];
}

function LeadDialog({ open, onOpenChange, editing, types }: LeadDialogProps) {
  const { toast } = useToast();
  const [form, setForm] = useState<Partial<SponsorLead>>({});

  useEffect(() => {
    if (open) setForm(editing ? { ...editing } : {});
  }, [open, editing]);

  const set = (k: keyof SponsorLead, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: async (data: Partial<SponsorLead>) => {
      if (editing) return apiRequest("PATCH", `/api/sponsor-hub/leads/${editing.id}`, data);
      return apiRequest("POST", "/api/sponsor-hub/leads", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sponsor-hub/leads"] });
      toast({ title: editing ? "Lead updated" : "Lead created" });
      onOpenChange(false);
    },
    onError: (e: any) => toast({ title: "Error", description: e?.message, variant: "destructive" }),
  });

  const submit = () => {
    if (!form.leadName?.trim()) return toast({ title: "Lead name required", variant: "destructive" });
    mutation.mutate(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Sponsor Lead" : "Add Sponsor Lead"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2 space-y-1">
            <Label>Lead / Company Name *</Label>
            <Input value={form.leadName ?? ""} onChange={(e) => set("leadName", e.target.value)} data-testid="input-lead-name" />
          </div>
          <div className="space-y-1">
            <Label>Contact Name</Label>
            <Input value={form.contactName ?? ""} onChange={(e) => set("contactName", e.target.value)} data-testid="input-contact-name" />
          </div>
          <div className="space-y-1">
            <Label>Phone</Label>
            <Input value={form.phoneNumber ?? ""} onChange={(e) => set("phoneNumber", e.target.value)} data-testid="input-phone" />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>Email</Label>
            <Input value={form.emailAddress ?? ""} onChange={(e) => set("emailAddress", e.target.value)} data-testid="input-email" />
          </div>
          <div className="space-y-1">
            <Label>Sponsor Type</Label>
            <Select value={form.sponsorTypeId ?? "__none"} onValueChange={(v) => set("sponsorTypeId", v === "__none" ? null : v)}>
              <SelectTrigger data-testid="select-sponsor-type"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">None</SelectItem>
                {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Rating (0–10)</Label>
            <Input type="number" min={0} max={10} value={form.rating ?? ""} onChange={(e) => set("rating", e.target.value ? parseInt(e.target.value) : null)} data-testid="input-rating" />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>Notes</Label>
            <VoiceToTextTextarea value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="input-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={mutation.isPending} data-testid="button-save-lead">
            {mutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {editing ? "Save Changes" : "Create Lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Add/Edit Client Dialog ────────────────────────────────────────────────────
interface ClientDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing?: SponsorClient | null;
  types: SponsorType[];
}

function ClientDialog({ open, onOpenChange, editing, types }: ClientDialogProps) {
  const { toast } = useToast();
  const [form, setForm] = useState<Partial<SponsorClient>>({});

  useEffect(() => {
    if (open) setForm(editing ? { ...editing } : {});
  }, [open, editing]);

  const set = (k: keyof SponsorClient, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: async (data: Partial<SponsorClient>) => {
      if (editing) return apiRequest("PATCH", `/api/sponsor-hub/clients/${editing.id}`, data);
      return apiRequest("POST", "/api/sponsor-hub/clients", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sponsor-hub/clients"] });
      toast({ title: editing ? "Client updated" : "Client created" });
      onOpenChange(false);
    },
    onError: (e: any) => toast({ title: "Error", description: e?.message, variant: "destructive" }),
  });

  const submit = () => {
    if (!form.accountName?.trim()) return toast({ title: "Account name required", variant: "destructive" });
    mutation.mutate(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Sponsor Client" : "Add Sponsor Client"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2 space-y-1">
            <Label>Account Name *</Label>
            <Input value={form.accountName ?? ""} onChange={(e) => set("accountName", e.target.value)} data-testid="input-account-name" />
          </div>
          <div className="space-y-1">
            <Label>Contact Name</Label>
            <Input value={form.contactName ?? ""} onChange={(e) => set("contactName", e.target.value)} data-testid="input-contact-name" />
          </div>
          <div className="space-y-1">
            <Label>Phone</Label>
            <Input value={form.phoneNumber ?? ""} onChange={(e) => set("phoneNumber", e.target.value)} data-testid="input-phone" />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>Email</Label>
            <Input value={form.emailAddress ?? ""} onChange={(e) => set("emailAddress", e.target.value)} data-testid="input-email" />
          </div>
          <div className="space-y-1">
            <Label>Sponsor Type</Label>
            <Select value={form.sponsorTypeId ?? "__none"} onValueChange={(v) => set("sponsorTypeId", v === "__none" ? null : v)}>
              <SelectTrigger data-testid="select-sponsor-type"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">None</SelectItem>
                {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1">
            <Label>Notes</Label>
            <VoiceToTextTextarea value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="input-client-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={mutation.isPending} data-testid="button-save-client">
            {mutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {editing ? "Save Changes" : "Create Client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Sponsor Leads Spreadsheet ─────────────────────────────────────────────────
interface SponsorLeadsGridProps { types: SponsorType[] }

function SponsorLeadsGrid({ types }: SponsorLeadsGridProps) {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sort, setSort] = useState("createdAt");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [filterType, setFilterType] = useState<string>("__all");
  const [filterRegion, setFilterRegion] = useState<string>("__all");
  const [filterTalent, setFilterTalent] = useState<string>("__all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState<SponsorLead | null>(null);
  const [deleteIds, setDeleteIds] = useState<string[] | null>(null);
  const [includeConverted, setIncludeConverted] = useState(false);
  const [smartSpec, setSmartSpec] = useState<NlFilterSpec | null>(null);
  const [smartEnabled, setSmartEnabled] = useState(false);
  const [smartHydrated, setSmartHydrated] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: regionList = [] } = useQuery<Region[]>({ queryKey: ["/api/regions"], staleTime: 120_000 });
  const { data: talentList = [] } = useQuery<any[]>({ queryKey: ["/api/talent"], staleTime: 120_000 });
  const { data: profile } = useQuery<{ role: string; smartSearchEnabled?: boolean }>({ queryKey: ["/api/profile"] });
  const { data: aiBalance } = useQuery<{ enabled?: boolean }>({ queryKey: ["/api/ai/balance"] });
  const aiEnabledForWorkspace = !!aiBalance?.enabled;

  useEffect(() => {
    if (smartHydrated) return;
    if (typeof profile?.smartSearchEnabled === "boolean") {
      setSmartEnabled(profile.smartSearchEnabled);
      setSmartHydrated(true);
    }
  }, [profile?.smartSearchEnabled, smartHydrated]);

  const handleSmartEnabledChange = (enabled: boolean) => {
    setSmartEnabled(enabled);
    apiRequest("PATCH", "/api/profile", { smartSearchEnabled: enabled });
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const queryKey = ["/api/sponsor-hub/leads", debouncedSearch, sort, dir, includeConverted, filterType, filterRegion, filterTalent, smartSpec];

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery<{
    items: SponsorLead[]; nextCursor: string | null;
  }>({
    queryKey,
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 30_000,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set("limit", "100");
      if (pageParam) params.set("cursor", pageParam as string);
      const q = smartSpec?.q || debouncedSearch;
      if (q) params.set("q", q);
      if (includeConverted) params.set("includeConverted", "true");
      if (filterType !== "__all") params.set("sponsorTypeId", filterType);
      const regionParam = smartSpec?.regionIds?.[0] || (filterRegion !== "__all" ? filterRegion : undefined);
      if (regionParam) params.set("regionId", regionParam);
      const talentParam = smartSpec?.talentId || (filterTalent !== "__all" ? filterTalent : undefined);
      if (talentParam) params.set("talentId", talentParam);
      params.set("sort", sort);
      params.set("dir", dir);
      const res = await fetch(`/api/sponsor-hub/leads?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch sponsor leads");
      return res.json();
    },
  });

  const leads = useMemo(() => (data?.pages ?? []).flatMap((p) => p.items), [data]);

  const rowVirtualizer = useVirtualizer({
    count: leads.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 36,
    overscan: 15,
  });

  // Infinite scroll trigger
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage(); },
      { root: el, threshold: 0.1 }
    );
    const sentinel = el.querySelector("[data-sentinel]") as Element | null;
    if (sentinel) observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, leads.length]);

  const handleSort = (k: string) => {
    if (sort === k) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(k); setDir("desc"); }
  };

  const updateLead = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<SponsorLead> }) =>
      apiRequest("PATCH", `/api/sponsor-hub/leads/${id}`, updates),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/sponsor-hub/leads"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (ids: string[]) => apiRequest("DELETE", "/api/sponsor-hub/leads/bulk", { ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sponsor-hub/leads"] });
      setSelected(new Set());
      setDeleteIds(null);
      toast({ title: `Deleted ${deleteIds?.length} lead${(deleteIds?.length ?? 0) === 1 ? "" : "s"}` });
    },
  });

  const convertMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/sponsor-hub/leads/${id}/convert`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sponsor-hub/leads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/sponsor-hub/clients"] });
      toast({ title: "Lead converted to sponsor client" });
    },
    onError: (e: any) => toast({ title: "Convert failed", description: e?.message, variant: "destructive" }),
  });

  const allSelected = leads.length > 0 && leads.every((l) => selected.has(l.id));
  const someSelected = !allSelected && leads.some((l) => selected.has(l.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(leads.map((l) => l.id)));
  };

  const COL = LEAD_COL_WIDTHS;
  const totalWidth = Object.values(COL).reduce((a, b) => a + b, 0);

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b flex-shrink-0">
        <div className="flex-1 max-w-md">
          <SmartSearchBar
            page="sponsor_leads"
            searchQuery={search}
            onSearchQueryChange={setSearch}
            spec={smartSpec}
            onSpecChange={setSmartSpec}
            smartEnabled={smartEnabled}
            onSmartEnabledChange={handleSmartEnabledChange}
            aiEnabledForWorkspace={aiEnabledForWorkspace}
            placeholder="Search sponsor leads…"
            regions={regionList}
          />
        </div>

        {/* Sponsor Type filter */}
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="h-8 text-sm w-[130px]" data-testid="select-filter-type-leads">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All types</SelectItem>
            {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>

        {/* Region filter */}
        {regionList.length > 0 && (
          <Select value={filterRegion} onValueChange={setFilterRegion}>
            <SelectTrigger className="h-8 text-sm w-[120px]" data-testid="select-filter-region-leads">
              <SelectValue placeholder="All regions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All regions</SelectItem>
              {regionList.map((r: any) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {/* Talent filter */}
        {talentList.length > 0 && (
          <Select value={filterTalent} onValueChange={setFilterTalent}>
            <SelectTrigger className="h-8 text-sm w-[120px]" data-testid="select-filter-talent-leads">
              <SelectValue placeholder="All talent" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All talent</SelectItem>
              {talentList.map((t: any) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
          <Checkbox
            checked={includeConverted}
            onCheckedChange={(v) => setIncludeConverted(!!v)}
            data-testid="checkbox-include-converted"
          />
          Include converted
        </label>

        {selected.size > 0 && (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setDeleteIds(Array.from(selected))}
            data-testid="button-bulk-delete-leads"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Delete {selected.size}
          </Button>
        )}

        <div className="ml-auto">
          <Button size="sm" onClick={() => { setEditTarget(null); setShowAdd(true); }} data-testid="button-add-lead">
            <Plus className="w-4 h-4 mr-1" />Add Lead
          </Button>
        </div>
      </div>

      {/* Column headers */}
      <div
        className="flex border-b bg-muted/40 flex-shrink-0 text-xs font-medium"
        style={{ minWidth: totalWidth }}
      >
        <div style={{ width: COL.select }} className="flex items-center justify-center px-1 py-2">
          <Checkbox
            checked={someSelected ? "indeterminate" : allSelected}
            onCheckedChange={toggleAll}
            data-testid="checkbox-select-all-leads"
          />
        </div>
        {([
          { k: "leadName", label: "Lead Name" },
          { k: "contactName", label: "Contact" },
          { k: "phone", label: "Phone" },
          { k: "email", label: "Email" },
          { k: "sponsorType", label: "Type" },
          { k: "rating", label: "Rating" },
          { k: "rep", label: "Rep" },
          { k: "city", label: "City" },
          { k: "state", label: "St" },
          { k: "zip", label: "Zip" },
          { k: "optOut", label: "Opt Out" },
          { k: "status", label: "Status" },
          { k: "lastContacted", label: "Last Contact" },
          { k: "address", label: "Address" },
          { k: "region", label: "Region" },
          { k: "talent", label: "Talent" },
          { k: "notes", label: "Notes" },
        ] as const).map(({ k, label }) => (
          <div key={k} style={{ width: COL[k] }} className="flex items-center px-2 py-2 border-r border-border/50">
            <ColHeader label={label} sortKey={k} sort={sort} dir={dir} onSort={handleSort} />
          </div>
        ))}
        <div className="flex items-center px-2 py-2 w-[60px]"></div>
      </div>

      {/* Virtual rows */}
      <div ref={scrollRef} className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…
          </div>
        ) : leads.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 gap-2 text-muted-foreground text-sm">
            <Handshake className="w-8 h-8 opacity-30" />
            <p>No sponsor leads found.</p>
            <Button size="sm" variant="outline" onClick={() => setShowAdd(true)}>Add first lead</Button>
          </div>
        ) : (
          <div style={{ height: rowVirtualizer.getTotalSize(), position: "relative", minWidth: totalWidth }}>
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const lead = leads[virtualRow.index];
              if (!lead) return null;
              const isSel = selected.has(lead.id);
              return (
                <div
                  key={lead.id}
                  data-testid={`row-sponsor-lead-${lead.id}`}
                  style={{
                    position: "absolute",
                    top: virtualRow.start,
                    height: virtualRow.size,
                    width: "100%",
                  }}
                  className={cn(
                    "flex items-center border-b border-border/40 hover:bg-muted/20 group",
                    isSel && "bg-primary/5"
                  )}
                >
                  {/* Select */}
                  <div style={{ width: COL.select }} className="flex items-center justify-center px-1 h-full">
                    <Checkbox
                      checked={isSel}
                      onCheckedChange={(v) => setSelected((s) => {
                        const n = new Set(s);
                        if (v) n.add(lead.id); else n.delete(lead.id);
                        return n;
                      })}
                      data-testid={`checkbox-lead-${lead.id}`}
                    />
                  </div>
                  {/* Lead Name */}
                  <div style={{ width: COL.leadName }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput
                      value={lead.leadName ?? ""}
                      onBlur={(v) => updateLead.mutate({ id: lead.id, updates: { leadName: v } })}
                      placeholder="Lead name"
                    />
                  </div>
                  {/* Contact */}
                  <div style={{ width: COL.contactName }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput
                      value={lead.contactName ?? ""}
                      onBlur={(v) => updateLead.mutate({ id: lead.id, updates: { contactName: v } })}
                      placeholder="Contact"
                    />
                  </div>
                  {/* Phone */}
                  <div style={{ width: COL.phone }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput
                      value={lead.phoneNumber ?? ""}
                      onBlur={(v) => updateLead.mutate({ id: lead.id, updates: { phoneNumber: v } })}
                      placeholder="Phone"
                    />
                  </div>
                  {/* Email */}
                  <div style={{ width: COL.email }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput
                      value={lead.emailAddress ?? ""}
                      onBlur={(v) => updateLead.mutate({ id: lead.id, updates: { emailAddress: v } })}
                      placeholder="Email"
                    />
                  </div>
                  {/* Type */}
                  <div style={{ width: COL.sponsorType }} className="h-full border-r border-border/40 flex items-center px-1">
                    <Select
                      value={lead.sponsorTypeId ?? "__none"}
                      onValueChange={(v) => updateLead.mutate({ id: lead.id, updates: { sponsorTypeId: v === "__none" ? null : v } })}
                    >
                      <SelectTrigger className="h-7 border-0 rounded-none text-xs focus:ring-0 focus:ring-offset-0 w-full">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">—</SelectItem>
                        {types.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`option-type-${t.id}`}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Rating */}
                  <div style={{ width: COL.rating }} className="h-full border-r border-border/40 flex items-center">
                    <input
                      type="number" min={0} max={10}
                      className="w-full h-full px-2 text-sm bg-transparent border-0 outline-none focus:ring-1 focus:ring-primary/50 rounded-none"
                      value={lead.rating ?? ""}
                      onChange={(e) => {
                        const n = parseInt(e.target.value);
                        if (!isNaN(n)) updateLead.mutate({ id: lead.id, updates: { rating: Math.max(0, Math.min(10, n)) } });
                        else if (e.target.value === "") updateLead.mutate({ id: lead.id, updates: { rating: null } });
                      }}
                      placeholder="—"
                      data-testid={`input-rating-${lead.id}`}
                    />
                  </div>
                  {/* Rep */}
                  <div style={{ width: COL.rep }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput value={lead.rep ?? ""} onBlur={(v) => updateLead.mutate({ id: lead.id, updates: { rep: v } })} placeholder="Rep" />
                  </div>
                  {/* City */}
                  <div style={{ width: COL.city }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput value={lead.city ?? ""} onBlur={(v) => updateLead.mutate({ id: lead.id, updates: { city: v } })} placeholder="City" />
                  </div>
                  {/* State */}
                  <div style={{ width: COL.state }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput value={lead.state ?? ""} onBlur={(v) => updateLead.mutate({ id: lead.id, updates: { state: v } })} placeholder="St" />
                  </div>
                  {/* Zip */}
                  <div style={{ width: COL.zip }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput value={lead.zipCode ?? ""} onBlur={(v) => updateLead.mutate({ id: lead.id, updates: { zipCode: v } })} placeholder="Zip" />
                  </div>
                  {/* Opt Out */}
                  <div style={{ width: COL.optOut }} className="h-full border-r border-border/40 flex items-center justify-center">
                    <Checkbox
                      checked={lead.emailOptedOut}
                      onCheckedChange={(v) => updateLead.mutate({ id: lead.id, updates: { emailOptedOut: !!v } })}
                      data-testid={`checkbox-optout-lead-${lead.id}`}
                    />
                  </div>
                  {/* Status */}
                  <div style={{ width: COL.status }} className="h-full border-r border-border/40 flex items-center px-2">
                    {lead.convertedAt ? (
                      <Badge variant="outline" className="text-xs text-green-600 border-green-600">
                        <CheckCircle2 className="w-3 h-3 mr-1" />Converted
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">Active</Badge>
                    )}
                  </div>
                  {/* Last Contacted */}
                  <div style={{ width: COL.lastContacted }} className="h-full border-r border-border/40 flex items-center px-2 text-xs text-muted-foreground">
                    {lead.lastContacted ? new Date(lead.lastContacted).toLocaleDateString() : "—"}
                  </div>
                  {/* Address */}
                  <div style={{ width: COL.address }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput value={lead.address ?? ""} onBlur={(v) => updateLead.mutate({ id: lead.id, updates: { address: v } })} placeholder="Address" />
                  </div>
                  {/* Region */}
                  <div style={{ width: COL.region }} className="h-full border-r border-border/40 flex items-center px-1">
                    <Select
                      value={lead.regionId ?? "__none"}
                      onValueChange={(v) => updateLead.mutate({ id: lead.id, updates: { regionId: v === "__none" ? null : v } })}
                    >
                      <SelectTrigger className="h-7 border-0 rounded-none text-xs focus:ring-0 focus:ring-offset-0 w-full">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">—</SelectItem>
                        {regionList.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Talent */}
                  <div style={{ width: COL.talent }} className="h-full border-r border-border/40 flex items-center px-1">
                    <Select
                      value={lead.talentId ?? "__none"}
                      onValueChange={(v) => updateLead.mutate({ id: lead.id, updates: { talentId: v === "__none" ? null : v } })}
                    >
                      <SelectTrigger className="h-7 border-0 rounded-none text-xs focus:ring-0 focus:ring-offset-0 w-full">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">—</SelectItem>
                        {talentList.map((t: any) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Notes */}
                  <div style={{ width: COL.notes }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput value={lead.notes ?? ""} onBlur={(v) => updateLead.mutate({ id: lead.id, updates: { notes: v } })} placeholder="Notes" />
                  </div>
                  {/* Actions */}
                  <div className="w-[60px] h-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6" data-testid={`button-lead-actions-${lead.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setEditTarget(lead); setShowAdd(true); }}>
                          Edit
                        </DropdownMenuItem>
                        {!lead.convertedAt && (
                          <DropdownMenuItem onClick={() => convertMutation.mutate(lead.id)} data-testid={`button-convert-lead-${lead.id}`}>
                            <ArrowRightCircle className="w-4 h-4 mr-2" />Convert to Client
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => setDeleteIds([lead.id])}
                          data-testid={`button-delete-lead-${lead.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
            <div data-sentinel style={{ position: "absolute", bottom: 0, height: 1, width: "100%" }} />
          </div>
        )}
        {isFetchingNextPage && (
          <div className="flex items-center justify-center py-3 text-muted-foreground text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />Loading more…
          </div>
        )}
      </div>

      {/* Footer count */}
      <div className="px-4 py-2 border-t text-xs text-muted-foreground flex-shrink-0">
        {leads.length} lead{leads.length !== 1 ? "s" : ""}
        {hasNextPage ? " (scroll for more)" : ""}
        {selected.size > 0 && ` · ${selected.size} selected`}
      </div>

      <LeadDialog
        open={showAdd}
        onOpenChange={(v) => { setShowAdd(v); if (!v) setEditTarget(null); }}
        editing={editTarget}
        types={types}
      />

      <AlertDialog open={!!deleteIds} onOpenChange={(v) => !v && setDeleteIds(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteIds?.length} lead{(deleteIds?.length ?? 0) !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => { if (deleteIds) deleteMutation.mutate(deleteIds); }}
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Sponsor Clients Spreadsheet ───────────────────────────────────────────────
interface SponsorClientsGridProps { types: SponsorType[] }

function SponsorClientsGrid({ types }: SponsorClientsGridProps) {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sort, setSort] = useState("createdAt");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [filterType, setFilterType] = useState<string>("__all");
  const [filterRegion, setFilterRegion] = useState<string>("__all");
  const [filterTalent, setFilterTalent] = useState<string>("__all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState<SponsorClient | null>(null);
  const [deleteIds, setDeleteIds] = useState<string[] | null>(null);
  const [smartSpec, setSmartSpec] = useState<NlFilterSpec | null>(null);
  const [smartEnabled, setSmartEnabled] = useState(false);
  const [smartHydrated, setSmartHydrated] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: regionList = [] } = useQuery<Region[]>({ queryKey: ["/api/regions"], staleTime: 120_000 });
  const { data: talentList = [] } = useQuery<any[]>({ queryKey: ["/api/talent"], staleTime: 120_000 });
  const { data: profile } = useQuery<{ role: string; smartSearchEnabled?: boolean }>({ queryKey: ["/api/profile"] });
  const { data: aiBalance } = useQuery<{ enabled?: boolean }>({ queryKey: ["/api/ai/balance"] });
  const aiEnabledForWorkspace = !!aiBalance?.enabled;

  useEffect(() => {
    if (smartHydrated) return;
    if (typeof profile?.smartSearchEnabled === "boolean") {
      setSmartEnabled(profile.smartSearchEnabled);
      setSmartHydrated(true);
    }
  }, [profile?.smartSearchEnabled, smartHydrated]);

  const handleSmartEnabledChange = (enabled: boolean) => {
    setSmartEnabled(enabled);
    apiRequest("PATCH", "/api/profile", { smartSearchEnabled: enabled });
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const queryKey = ["/api/sponsor-hub/clients", debouncedSearch, sort, dir, filterType, filterRegion, filterTalent, smartSpec];

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery<{
    items: SponsorClient[]; nextCursor: string | null;
  }>({
    queryKey,
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 30_000,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set("limit", "100");
      if (pageParam) params.set("cursor", pageParam as string);
      const q = smartSpec?.q || debouncedSearch;
      if (q) params.set("q", q);
      if (filterType !== "__all") params.set("sponsorTypeId", filterType);
      const regionParam = smartSpec?.regionIds?.[0] || (filterRegion !== "__all" ? filterRegion : undefined);
      if (regionParam) params.set("regionId", regionParam);
      const talentParam = smartSpec?.talentId || (filterTalent !== "__all" ? filterTalent : undefined);
      if (talentParam) params.set("talentId", talentParam);
      params.set("sort", sort);
      params.set("dir", dir);
      const res = await fetch(`/api/sponsor-hub/clients?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch sponsor clients");
      return res.json();
    },
  });

  const clients = useMemo(() => (data?.pages ?? []).flatMap((p) => p.items), [data]);

  const rowVirtualizer = useVirtualizer({
    count: clients.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 36,
    overscan: 15,
  });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage(); },
      { root: el, threshold: 0.1 }
    );
    const sentinel = el.querySelector("[data-sentinel]") as Element | null;
    if (sentinel) observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, clients.length]);

  const handleSort = (k: string) => {
    if (sort === k) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(k); setDir("desc"); }
  };

  const updateClient = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<SponsorClient> }) =>
      apiRequest("PATCH", `/api/sponsor-hub/clients/${id}`, updates),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/sponsor-hub/clients"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (ids: string[]) => apiRequest("DELETE", "/api/sponsor-hub/clients/bulk", { ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sponsor-hub/clients"] });
      setSelected(new Set());
      setDeleteIds(null);
      toast({ title: `Deleted ${deleteIds?.length} client${(deleteIds?.length ?? 0) === 1 ? "" : "s"}` });
    },
  });

  const allSelected = clients.length > 0 && clients.every((c) => selected.has(c.id));
  const someSelected = !allSelected && clients.some((c) => selected.has(c.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(clients.map((c) => c.id)));
  };

  const COL = CLIENT_COL_WIDTHS;
  const totalWidth = Object.values(COL).reduce((a, b) => a + b, 0);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-3 border-b flex-shrink-0">
        <div className="flex-1 max-w-md">
          <SmartSearchBar
            page="sponsor_clients"
            searchQuery={search}
            onSearchQueryChange={setSearch}
            spec={smartSpec}
            onSpecChange={setSmartSpec}
            smartEnabled={smartEnabled}
            onSmartEnabledChange={handleSmartEnabledChange}
            aiEnabledForWorkspace={aiEnabledForWorkspace}
            placeholder="Search sponsor clients…"
            regions={regionList}
          />
        </div>

        {/* Sponsor Type filter */}
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="h-8 text-sm w-[130px]" data-testid="select-filter-type-clients">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All types</SelectItem>
            {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>

        {/* Region filter */}
        {regionList.length > 0 && (
          <Select value={filterRegion} onValueChange={setFilterRegion}>
            <SelectTrigger className="h-8 text-sm w-[120px]" data-testid="select-filter-region-clients">
              <SelectValue placeholder="All regions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All regions</SelectItem>
              {regionList.map((r: any) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {/* Talent filter */}
        {talentList.length > 0 && (
          <Select value={filterTalent} onValueChange={setFilterTalent}>
            <SelectTrigger className="h-8 text-sm w-[120px]" data-testid="select-filter-talent-clients">
              <SelectValue placeholder="All talent" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All talent</SelectItem>
              {talentList.map((t: any) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {selected.size > 0 && (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setDeleteIds(Array.from(selected))}
            data-testid="button-bulk-delete-clients"
          >
            <Trash2 className="w-4 h-4 mr-1" />Delete {selected.size}
          </Button>
        )}

        <div className="ml-auto">
          <Button size="sm" onClick={() => { setEditTarget(null); setShowAdd(true); }} data-testid="button-add-client">
            <Plus className="w-4 h-4 mr-1" />Add Client
          </Button>
        </div>
      </div>

      <div
        className="flex border-b bg-muted/40 flex-shrink-0 text-xs font-medium"
        style={{ minWidth: totalWidth }}
      >
        <div style={{ width: COL.select }} className="flex items-center justify-center px-1 py-2">
          <Checkbox
            checked={someSelected ? "indeterminate" : allSelected}
            onCheckedChange={toggleAll}
            data-testid="checkbox-select-all-clients"
          />
        </div>
        {([
          { k: "accountName", label: "Account Name" },
          { k: "contactName", label: "Contact" },
          { k: "phone", label: "Phone" },
          { k: "email", label: "Email" },
          { k: "sponsorType", label: "Type" },
          { k: "rep", label: "Rep" },
          { k: "city", label: "City" },
          { k: "state", label: "St" },
          { k: "zip", label: "Zip" },
          { k: "optOut", label: "Opt Out" },
          { k: "notes", label: "Notes" },
          { k: "address", label: "Address" },
          { k: "region", label: "Region" },
          { k: "talent", label: "Talent" },
          { k: "associatedEvent", label: "Event ID" },
        ] as const).map(({ k, label }) => (
          <div key={k} style={{ width: COL[k] }} className="flex items-center px-2 py-2 border-r border-border/50">
            <ColHeader label={label} sortKey={k} sort={sort} dir={dir} onSort={handleSort} />
          </div>
        ))}
        <div className="flex items-center px-2 py-2 w-[60px]"></div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…
          </div>
        ) : clients.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 gap-2 text-muted-foreground text-sm">
            <Handshake className="w-8 h-8 opacity-30" />
            <p>No sponsor clients yet.</p>
            <Button size="sm" variant="outline" onClick={() => setShowAdd(true)}>Add first client</Button>
          </div>
        ) : (
          <div style={{ height: rowVirtualizer.getTotalSize(), position: "relative", minWidth: totalWidth }}>
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const client = clients[virtualRow.index];
              if (!client) return null;
              const isSel = selected.has(client.id);
              return (
                <div
                  key={client.id}
                  data-testid={`row-sponsor-client-${client.id}`}
                  style={{ position: "absolute", top: virtualRow.start, height: virtualRow.size, width: "100%" }}
                  className={cn("flex items-center border-b border-border/40 hover:bg-muted/20 group", isSel && "bg-primary/5")}
                >
                  <div style={{ width: COL.select }} className="flex items-center justify-center px-1 h-full">
                    <Checkbox
                      checked={isSel}
                      onCheckedChange={(v) => setSelected((s) => {
                        const n = new Set(s);
                        if (v) n.add(client.id); else n.delete(client.id);
                        return n;
                      })}
                      data-testid={`checkbox-client-${client.id}`}
                    />
                  </div>
                  <div style={{ width: COL.accountName }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput
                      value={client.accountName ?? ""}
                      onBlur={(v) => updateClient.mutate({ id: client.id, updates: { accountName: v } })}
                      placeholder="Account name"
                    />
                  </div>
                  <div style={{ width: COL.contactName }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput
                      value={client.contactName ?? ""}
                      onBlur={(v) => updateClient.mutate({ id: client.id, updates: { contactName: v } })}
                      placeholder="Contact"
                    />
                  </div>
                  <div style={{ width: COL.phone }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput
                      value={client.phoneNumber ?? ""}
                      onBlur={(v) => updateClient.mutate({ id: client.id, updates: { phoneNumber: v } })}
                      placeholder="Phone"
                    />
                  </div>
                  <div style={{ width: COL.email }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput
                      value={client.emailAddress ?? ""}
                      onBlur={(v) => updateClient.mutate({ id: client.id, updates: { emailAddress: v } })}
                      placeholder="Email"
                    />
                  </div>
                  <div style={{ width: COL.sponsorType }} className="h-full border-r border-border/40 flex items-center px-1">
                    <Select
                      value={client.sponsorTypeId ?? "__none"}
                      onValueChange={(v) => updateClient.mutate({ id: client.id, updates: { sponsorTypeId: v === "__none" ? null : v } })}
                    >
                      <SelectTrigger className="h-7 border-0 rounded-none text-xs focus:ring-0 focus:ring-offset-0 w-full">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">—</SelectItem>
                        {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Rep */}
                  <div style={{ width: COL.rep }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput value={client.rep ?? ""} onBlur={(v) => updateClient.mutate({ id: client.id, updates: { rep: v } })} placeholder="Rep" />
                  </div>
                  {/* City */}
                  <div style={{ width: COL.city }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput value={client.city ?? ""} onBlur={(v) => updateClient.mutate({ id: client.id, updates: { city: v } })} placeholder="City" />
                  </div>
                  {/* State */}
                  <div style={{ width: COL.state }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput value={client.state ?? ""} onBlur={(v) => updateClient.mutate({ id: client.id, updates: { state: v } })} placeholder="St" />
                  </div>
                  {/* Zip */}
                  <div style={{ width: COL.zip }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput value={client.zipCode ?? ""} onBlur={(v) => updateClient.mutate({ id: client.id, updates: { zipCode: v } })} placeholder="Zip" />
                  </div>
                  {/* Opt Out */}
                  <div style={{ width: COL.optOut }} className="h-full border-r border-border/40 flex items-center justify-center">
                    <Checkbox
                      checked={client.emailOptedOut}
                      onCheckedChange={(v) => updateClient.mutate({ id: client.id, updates: { emailOptedOut: !!v } })}
                      data-testid={`checkbox-optout-client-${client.id}`}
                    />
                  </div>
                  <div style={{ width: COL.notes }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput
                      value={client.notes ?? ""}
                      onBlur={(v) => updateClient.mutate({ id: client.id, updates: { notes: v } })}
                      placeholder="Notes"
                    />
                  </div>
                  {/* Address */}
                  <div style={{ width: COL.address }} className="h-full border-r border-border/40 flex items-center">
                    <CellInput value={client.address ?? ""} onBlur={(v) => updateClient.mutate({ id: client.id, updates: { address: v } })} placeholder="Address" />
                  </div>
                  {/* Region */}
                  <div style={{ width: COL.region }} className="h-full border-r border-border/40 flex items-center px-1">
                    <Select
                      value={client.regionId ?? "__none"}
                      onValueChange={(v) => updateClient.mutate({ id: client.id, updates: { regionId: v === "__none" ? null : v } })}
                    >
                      <SelectTrigger className="h-7 border-0 rounded-none text-xs focus:ring-0 focus:ring-offset-0 w-full">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">—</SelectItem>
                        {regionList.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Talent */}
                  <div style={{ width: COL.talent }} className="h-full border-r border-border/40 flex items-center px-1">
                    <Select
                      value={client.talentId ?? "__none"}
                      onValueChange={(v) => updateClient.mutate({ id: client.id, updates: { talentId: v === "__none" ? null : v } })}
                    >
                      <SelectTrigger className="h-7 border-0 rounded-none text-xs focus:ring-0 focus:ring-offset-0 w-full">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">—</SelectItem>
                        {talentList.map((t: any) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Associated Event ID */}
                  <div style={{ width: COL.associatedEvent }} className="h-full border-r border-border/40 flex items-center px-2 text-xs text-muted-foreground font-mono truncate">
                    {client.associatedEventId ? client.associatedEventId.slice(0, 12) + "…" : "—"}
                  </div>
                  <div className="w-[60px] h-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6" data-testid={`button-client-actions-${client.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setEditTarget(client); setShowAdd(true); }}>Edit</DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => setDeleteIds([client.id])}
                          data-testid={`button-delete-client-${client.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
            <div data-sentinel style={{ position: "absolute", bottom: 0, height: 1, width: "100%" }} />
          </div>
        )}
      </div>

      <div className="px-4 py-2 border-t text-xs text-muted-foreground flex-shrink-0">
        {clients.length} client{clients.length !== 1 ? "s" : ""}
        {hasNextPage ? " (scroll for more)" : ""}
        {selected.size > 0 && ` · ${selected.size} selected`}
      </div>

      <ClientDialog
        open={showAdd}
        onOpenChange={(v) => { setShowAdd(v); if (!v) setEditTarget(null); }}
        editing={editTarget}
        types={types}
      />

      <AlertDialog open={!!deleteIds} onOpenChange={(v) => !v && setDeleteIds(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteIds?.length} client{(deleteIds?.length ?? 0) !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => { if (deleteIds) deleteMutation.mutate(deleteIds); }}
              data-testid="button-confirm-delete-client"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Add Sponsor Invoice Dialog ────────────────────────────────────────────────
interface AddInvoiceDialogProps { open: boolean; onOpenChange: (v: boolean) => void }

function AddInvoiceDialog({ open, onOpenChange }: AddInvoiceDialogProps) {
  const { toast } = useToast();
  const [form, setForm] = useState<Record<string, string>>({});
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: (data: Record<string, any>) => apiRequest("POST", "/api/sponsor-hub/invoices", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sponsor-hub/invoices"] });
      toast({ title: "Sponsor invoice created" });
      onOpenChange(false);
      setForm({});
    },
    onError: (e: any) => toast({ title: "Error", description: e?.message, variant: "destructive" }),
  });

  const submit = () => {
    if (!form.accountName?.trim()) return toast({ title: "Account name required", variant: "destructive" });
    mutation.mutate({
      accountName: form.accountName,
      invoiceDate: form.invoiceDate || new Date().toISOString().slice(0, 10),
      eventDate: form.eventDate || null,
      amountCents: form.amountCents ? Math.round(parseFloat(form.amountCents) * 100) : 0,
      notes: form.notes || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New Sponsor Invoice</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label>Account Name *</Label>
            <Input value={form.accountName ?? ""} onChange={(e) => set("accountName", e.target.value)} data-testid="input-invoice-account" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Invoice Date</Label>
              <Input type="date" value={form.invoiceDate ?? ""} onChange={(e) => set("invoiceDate", e.target.value)} data-testid="input-invoice-date" />
            </div>
            <div className="space-y-1">
              <Label>Event Date</Label>
              <Input type="date" value={form.eventDate ?? ""} onChange={(e) => set("eventDate", e.target.value)} data-testid="input-event-date" />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Amount ($)</Label>
            <Input type="number" min={0} step="0.01" value={form.amountCents ?? ""} onChange={(e) => set("amountCents", e.target.value)} placeholder="0.00" data-testid="input-invoice-amount" />
          </div>
          <div className="space-y-1">
            <Label>Notes</Label>
            <VoiceToTextTextarea value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="input-invoice-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={mutation.isPending} data-testid="button-create-sponsor-invoice">
            {mutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Create Invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Sponsor Invoices Table ────────────────────────────────────────────────────
function SponsorInvoicesSection() {
  const [showAdd, setShowAdd] = useState(false);
  const [filterStatus, setFilterStatus] = useState<"all" | "paid" | "unpaid" | "overdue">("all");
  const [filterYear, setFilterYear] = useState<string>("all");

  const { data: invoices = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/sponsor-hub/invoices", filterStatus, filterYear],
    staleTime: 30_000,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterYear !== "all") params.set("year", filterYear);
      const res = await fetch(`/api/sponsor-hub/invoices?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch sponsor invoices");
      return res.json();
    },
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => String(currentYear - i));

  const formatMoney = (rate: string | number | null | undefined) => {
    if (rate == null || rate === "") return "—";
    const n = typeof rate === "number" ? rate : parseFloat(rate);
    if (isNaN(n)) return "—";
    return `$${n.toFixed(2)}`;
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-3 border-b flex-shrink-0">
        {/* Status filter */}
        <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as any)}>
          <SelectTrigger className="h-8 text-sm w-[120px]" data-testid="select-invoice-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
        {/* Year filter */}
        <Select value={filterYear} onValueChange={setFilterYear}>
          <SelectTrigger className="h-8 text-sm w-[100px]" data-testid="select-invoice-year">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All years</SelectItem>
            {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto">
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="button-add-sponsor-invoice">
            <Plus className="w-4 h-4 mr-1" />New Invoice
          </Button>
        </div>
      </div>

      {/* Table header */}
      <div className="flex border-b bg-muted/40 text-xs font-medium text-muted-foreground flex-shrink-0">
        {["Invoice #", "Account", "Event Date", "Invoice Date", "Amount", "Status"].map((h) => (
          <div key={h} className="px-3 py-2 flex-1 border-r border-border/50 last:border-r-0">{h}</div>
        ))}
      </div>

      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…
          </div>
        ) : invoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 gap-2 text-muted-foreground text-sm">
            <FileText className="w-8 h-8 opacity-30" />
            <p>No sponsor invoices yet.</p>
            <Button size="sm" variant="outline" onClick={() => setShowAdd(true)}>Create first invoice</Button>
          </div>
        ) : (
          invoices.map((inv: any) => (
            <div key={inv.id} className="flex border-b border-border/40 hover:bg-muted/20 text-sm" data-testid={`row-sponsor-invoice-${inv.id}`}>
              <div className="px-3 py-2 flex-1 border-r border-border/30 font-mono text-xs truncate">{inv.invoiceNumber || inv.id?.slice(0, 8)}</div>
              <div className="px-3 py-2 flex-1 border-r border-border/30 truncate">{inv.accountName || "—"}</div>
              <div className="px-3 py-2 flex-1 border-r border-border/30 text-muted-foreground">
                {inv.dateOfEvent ? new Date(inv.dateOfEvent).toLocaleDateString() : "—"}
              </div>
              <div className="px-3 py-2 flex-1 border-r border-border/30 text-muted-foreground">
                {inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : "—"}
              </div>
              <div className="px-3 py-2 flex-1 border-r border-border/30">{formatMoney(inv.eventRate)}</div>
              <div className="px-3 py-2 flex-1">
                {inv.datePaid ? (
                  <Badge className="text-xs bg-green-100 text-green-700 border-green-300">Paid</Badge>
                ) : inv.dateOfEvent && new Date(inv.dateOfEvent) < new Date() ? (
                  <Badge className="text-xs bg-red-100 text-red-700 border-red-300">Overdue</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">{inv.status || "pending"}</Badge>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="px-4 py-2 border-t text-xs text-muted-foreground flex-shrink-0">
        {invoices.length} invoice{invoices.length !== 1 ? "s" : ""}
      </div>

      <AddInvoiceDialog open={showAdd} onOpenChange={setShowAdd} />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function SponsorHubPage() {
  const { data: accessData, isLoading: accessLoading } = useSponsorHubAccess();
  const enabled = accessData?.enabled === true;
  const [typesDialogOpen, setTypesDialogOpen] = useState(false);

  const { data: types = [] } = useQuery<SponsorType[]>({
    queryKey: ["/api/sponsor-hub/types"],
    enabled,
    staleTime: 60_000,
  });

  if (accessLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[300px] text-muted-foreground gap-2">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading Sponsor Hub…
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-4 text-center px-4">
        <Handshake className="w-12 h-12 text-muted-foreground opacity-40" />
        <h2 className="text-lg font-semibold">Sponsor Hub Not Enabled</h2>
        <p className="text-sm text-muted-foreground max-w-sm">
          The Sponsor Hub module is not enabled for this workspace.
          Contact your platform administrator to enable it.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Page header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b flex-shrink-0">
        <Handshake className="w-5 h-5 text-primary" />
        <h1 className="text-lg font-semibold">Sponsor Hub</h1>
        <Badge variant="outline" className="text-xs">Marketing</Badge>
        <Button
          variant="ghost"
          size="icon"
          className="ml-auto"
          onClick={() => setTypesDialogOpen(true)}
          title="Manage sponsor types"
          aria-label="Manage sponsor types"
          data-testid="button-manage-sponsor-types"
        >
          <Settings className="w-4 h-4" />
        </Button>
      </div>

      <ManageSponsorTypesDialog open={typesDialogOpen} onOpenChange={setTypesDialogOpen} />

      <Tabs defaultValue="leads" className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="mx-6 mt-3 mb-0 w-fit flex-shrink-0" data-testid="tabs-sponsor-hub">
          <TabsTrigger value="leads" data-testid="tab-sponsor-leads">Sponsor Leads</TabsTrigger>
          <TabsTrigger value="clients" data-testid="tab-sponsor-clients">Sponsor Clients</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-sponsor-invoices">Sponsor Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="leads" className="flex-1 overflow-hidden mt-0 pt-0">
          <SponsorLeadsGrid types={types} />
        </TabsContent>
        <TabsContent value="clients" className="flex-1 overflow-hidden mt-0 pt-0">
          <SponsorClientsGrid types={types} />
        </TabsContent>
        <TabsContent value="invoices" className="flex-1 overflow-hidden mt-0 pt-0">
          <SponsorInvoicesSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
